import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { TrendingUp, User, Search } from "lucide-react";

import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { usePagination } from "../hooks/usePagination";
import TablePagination from "../components/TablePagination";
import { Input } from "@/components/ui/input";
import PropTypes from 'prop-types';

export default function ReferralDoctors({ onLogout }) {
  const navigate = useNavigate();
  const doctorId = localStorage.getItem('user_id');
  const [searchTerm, setSearchTerm] = useState("");
  const doctors = useLiveQuery(async () => {
    if (!doctorId) return [];

    // Get referral doctors via Fee Agreements
    const rules = await db.fee_agreements.where('user_id').equals(doctorId).toArray();

    // Get unique referral doctor IDs from rules
    const referralDoctorIds = [...new Set(
      rules
        .filter(r => r.referral_doctor_id)
        .map(r => r.referral_doctor_id)
    )];

    // Also get doctors created by this user
    const createdDoctors = await db.users.where('created_by_user_id').equals(doctorId).toArray();
    const createdDoctorIds = createdDoctors.map(d => d.id);

    // Combine and dedupe
    const allDoctorIds = [...new Set([...referralDoctorIds, ...createdDoctorIds])];

    // Fetch memberships for these users to check roles
    const memberships = await db.clinic_members.where('user_id').anyOf(allDoctorIds).toArray();

    // Map user_id -> set of roles
    const userRoles = {};
    memberships.forEach(m => {
      if (!userRoles[m.user_id]) userRoles[m.user_id] = new Set();
      userRoles[m.user_id].add(m.role);
    });

    // Fetch all relevant doctors
    const doctors = await db.users.where('id').anyOf(allDoctorIds).toArray();

    // Filter out self and receptionist-only users
    return doctors.filter(d => {
      if (d.id === doctorId) return false;

      const roles = userRoles[d.id];
      if (roles && roles.size > 0) {
        // If they have roles, check if they are ONLY a receptionist
        // If they have 'doctor' or 'admin', keep them.
        const isDocOrAdmin = roles.has('doctor') || roles.has('admin');
        if (!isDocOrAdmin && roles.has('receptionist')) return false;
      }

      return true;
    });
  }, [doctorId]) || [];

  const filteredDoctors = useMemo(() => {
    if (!searchTerm) return doctors;
    const term = searchTerm.toLowerCase();
    return doctors.filter(d =>
      d.name.toLowerCase().includes(term) ||
      d.phone.includes(term) ||
      d.city?.toLowerCase().includes(term)
    );
  }, [doctors, searchTerm]);

  const pagination = usePagination(filteredDoctors, {
    initialPageSize: 15,
    isMobile: window.innerWidth < 768
  });

  return (
    <div className="page-container" data-testid="referrals-page">
      <Sidebar active="referrals" onLogout={onLogout} />
      <div className="main-content">
        <div className="header">
          <h1>Associated Doctors</h1>
        </div>

        <div className="search-bar mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, phone or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="doctor-search-input"
            />
          </div>
        </div>

        {doctors.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><TrendingUp /></div>
            <h3>No associated doctors</h3>
            <p>Add fee agreements or stub clinics to populate this list</p>
          </div>
        ) : (
          <div className="card">
            {/* Desktop Table View */}
            <div className="table-container desktop-only">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Qualification</th>
                    <th>City</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.items.map((doctor) => (
                    <tr key={doctor.id} data-testid={`doctor-row-${doctor.id}`} onClick={() => navigate(`/referrals/${doctor.id}`)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{ width: '32px', height: '32px', background: '#dbeafe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <User size={16} color="#1e40af" />
                          </div>
                          {doctor.name}
                        </div>
                      </td>
                      <td>{doctor.phone}</td>
                      <td>{doctor.email?.includes('placeholder') ? '-' : doctor.email}</td>
                      <td>{doctor.qualification || '-'}</td>
                      <td>{doctor.city || '-'}</td>
                      <td>
                        {doctor.is_registered ? (
                          <span className="badge badge-completed">Registered</span>
                        ) : (
                          <span className="badge badge-pending">Placeholder</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="mobile-only">
              {pagination.items.map((doctor) => (
                <div
                  key={doctor.id}
                  data-testid={`doctor-card-${doctor.id}`}
                  onClick={() => navigate(`/referrals/${doctor.id}`)}
                  style={{
                    padding: '1rem',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    marginBottom: '0.75rem',
                    borderLeft: `3px solid ${doctor.is_registered ? '#10b981' : '#fbbf24'}`,
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div style={{ width: '48px', height: '48px', background: '#dbeafe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <User size={24} color="#1e40af" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '1.125rem', marginBottom: '0.25rem' }}>
                        {doctor.name}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        📞 {doctor.phone}
                      </div>
                    </div>
                    {doctor.is_registered ? (
                      <span className="badge badge-completed">Registered</span>
                    ) : (
                      <span className="badge badge-pending">Placeholder</span>
                    )}
                  </div>
                  {doctor.email && !doctor.email.includes('placeholder') && (
                    <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>
                      ✉️ {doctor.email}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.875rem' }}>
                    {doctor.qualification && (
                      <div>
                        <span style={{ color: '#94a3b8' }}>Qualification:</span>
                        <div style={{ fontWeight: '500' }}>{doctor.qualification}</div>
                      </div>
                    )}
                    {doctor.city && (
                      <div>
                        <span style={{ color: '#94a3b8' }}>City:</span>
                        <div style={{ fontWeight: '500' }}>{doctor.city}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <TablePagination
              pagination={pagination}
              isMobile={window.innerWidth < 768}
            />
          </div>
        )}
      </div>
    </div>
  );
}

ReferralDoctors.propTypes = {
  onLogout: PropTypes.func
};